'use client';

import { useCallback, useEffect, useState } from 'react';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import {
  mobileGraphql,
  mobileKeys,
  type MobileConnection,
  type MobileFrontEndMapView,
  type MobileUiConfigArtifact,
} from '@/shared/mobile-service/api/cors-mobile-api';
import {
  buildMapPointsQuery,
  mapDetailFieldNames,
  mapFieldSelection,
  mapPointIdField,
  mapRootQuery,
} from '../lib/build-map-query';
import { mapRowsToPoints, type MapPoint } from '../lib/map-points';
import { compileWhere, type FilterState } from '../lib/where';

export type { MapPoint } from '../lib/map-points';

/** Max rows per page accepted by mobile-service list queries. */
const PAGE_LIMIT = 500;
/** Plottable points required before the map is shown, and between later redraws. */
const MAP_BATCH = 1_000;
/** Pages kept in flight after the first response reports the total. */
const PAGE_CONCURRENCY = 4;
/**
 * Safety ceiling so a pathological acquisition cannot exhaust the tab.
 * Clustering stays on; hitting this before the list ends is reported as partial.
 */
const MAX_POINTS = 200_000;

/**
 * How many plottable points the map should draw.
 *
 * Nothing is drawn until {@link MAP_BATCH} points have arrived, unless the list
 * is already finished with fewer. Later redraws land on batch boundaries, and
 * the final publish includes the remainder.
 */
export function visibleMapPointCount(loaded: number, done: boolean): number {
  if (loaded <= 0) return 0;
  if (done) return loaded;
  const floored = Math.floor(loaded / MAP_BATCH) * MAP_BATCH;
  return floored >= MAP_BATCH ? floored : 0;
}

/** Plottable points plus how far the paged fetch has gotten. */
export interface MapPointsResult {
  /** Points the map is allowed to draw. A prefix of {@link loadedCount}. */
  points: MapPoint[];
  /** Plottable points fetched so far, including ones not yet drawn. */
  loadedCount: number;
  /** Total rows reported by the server for the artifact. */
  total: number;
  /** True when fetching stopped at {@link MAX_POINTS} before exhausting the list. */
  truncated: boolean;
  /** True until the map can be shown, or the list is empty. */
  isLoading: boolean;
  /** True while later pages are still arriving after the map is already showing. */
  isStreaming: boolean;
  error: Error | null;
  refetch: () => void;
}

interface UseMapPointsArgs {
  mobileAcquisitionId: number;
  view: MobileFrontEndMapView;
  artifact: MobileUiConfigArtifact;
  filters?: FilterState;
  allowedFields?: ReadonlySet<string>;
  enabled?: boolean;
}

const EMPTY: Omit<MapPointsResult, 'refetch'> = {
  points: [],
  loadedCount: 0,
  total: 0,
  truncated: false,
  isLoading: true,
  isStreaming: false,
  error: null,
};

/**
 * Fetch every artifact row with a valid coordinate for the map view.
 *
 * Pages 500 rows at a time. The first page runs alone so the total is known;
 * later pages run {@link PAGE_CONCURRENCY} at a time and are applied in offset
 * order. The loading count updates after every applied page. The map stays
 * hidden until {@link MAP_BATCH} plottable points (or the whole list, if it is
 * shorter), then redraws on each further batch boundary. Stops when the
 * filtered list is exhausted or {@link MAX_POINTS} is hit.
 */
export function useMapPoints(args: UseMapPointsArgs): MapPointsResult {
  const { cellId, accessToken, enabled: sessionEnabled } = useCellServiceSession();
  const {
    mobileAcquisitionId,
    view,
    artifact,
    filters = {},
    allowedFields,
    enabled = true,
  } = args;
  const [reloadKey, setReloadKey] = useState(0);
  const [result, setResult] = useState<Omit<MapPointsResult, 'refetch'>>(EMPTY);

  const rootQuery = mapRootQuery(artifact);
  const idField = mapPointIdField(artifact);
  const locations = artifact.locations;
  const detailFields = mapDetailFieldNames(view, artifact);
  const selection = mapFieldSelection(view, artifact, detailFields);
  const where = compileWhere(filters, allowedFields);
  const whereKey = JSON.stringify(where ?? null);
  const queryKey = mobileKeys.mapPoints(
    cellId,
    mobileAcquisitionId,
    artifact.graphql_type,
    whereKey,
  );

  const refetch = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    if (!sessionEnabled || !enabled || !rootQuery || !cellId || !accessToken) {
      setResult({
        ...EMPTY,
        isLoading: Boolean(sessionEnabled === false || !cellId || !accessToken) && enabled,
      });
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    const points: MapPoint[] = [];
    const buffered = new Map<number, { items: Record<string, unknown>[]; total: number }>();
    const inFlight = new Set<number>();
    let nextOffset = 0;
    let contiguousOffset = 0;
    let total = 0;
    let totalKnown = false;
    let truncated = false;
    let finished = false;

    setResult({ ...EMPTY, isLoading: true });

    const publish = (done: boolean) => {
      if (cancelled) return;
      const visible = visibleMapPointCount(points.length, done);
      setResult((prev) => ({
        points: visible !== prev.points.length || done ? points.slice(0, visible) : prev.points,
        loadedCount: points.length,
        total,
        truncated,
        isLoading: visible === 0 && !done,
        isStreaming: !done,
        error: null,
      }));
    };

    const fail = (error: unknown) => {
      if (cancelled || finished || controller.signal.aborted) return;
      finished = true;
      controller.abort();
      const next = error instanceof Error ? error : new Error(String(error));
      const visible = visibleMapPointCount(points.length, true);
      setResult({
        points: points.slice(0, visible),
        loadedCount: points.length,
        total,
        truncated,
        isLoading: false,
        isStreaming: false,
        error: next,
      });
    };

    const fold = () => {
      while (buffered.has(contiguousOffset)) {
        const page = buffered.get(contiguousOffset)!;
        buffered.delete(contiguousOffset);
        total = page.total || total;
        const pagePoints = mapRowsToPoints(page.items, view, idField, contiguousOffset, locations);
        const room = MAX_POINTS - points.length;
        if (pagePoints.length > room) {
          if (room > 0) points.push(...pagePoints.slice(0, room));
          truncated = true;
          finished = true;
          publish(true);
          controller.abort();
          return;
        }
        points.push(...pagePoints);
        const exhausted =
          page.items.length < PAGE_LIMIT ||
          page.items.length === 0 ||
          contiguousOffset + page.items.length >= page.total;
        if (exhausted) {
          finished = true;
          publish(true);
          return;
        }
        contiguousOffset += PAGE_LIMIT;
        publish(false);
      }
    };

    const fetchPage = async (offset: number) => {
      const query = buildMapPointsQuery({
        rootQuery,
        mobileAcquisitionId,
        offset,
        limit: PAGE_LIMIT,
        selection,
        where,
      });
      if (!query) throw new Error('Invalid map root query');
      const data = await mobileGraphql<Record<string, MobileConnection>>({
        cellId,
        accessToken,
        endpoint: artifact.endpoint,
        query,
        signal: controller.signal,
      });
      if (cancelled || finished) return;
      const connection = data[rootQuery];
      if (!connection) {
        throw new Error(`Missing "${rootQuery}" in mobile-service response`);
      }
      if (!totalKnown) {
        total = connection.total ?? 0;
        totalKnown = true;
      }
      buffered.set(offset, {
        items: connection.items,
        total: connection.total ?? 0,
      });
      fold();
    };

    const pump = () => {
      if (finished || cancelled) return;
      // Stay a few pages ahead of the last applied offset. Without this, a slow
      // early page would let later completions schedule the rest of the list.
      const maxOffset = contiguousOffset + PAGE_CONCURRENCY * PAGE_LIMIT;
      while (
        inFlight.size < PAGE_CONCURRENCY &&
        nextOffset < maxOffset &&
        (totalKnown ? nextOffset < total : nextOffset === 0)
      ) {
        const offset = nextOffset;
        nextOffset += PAGE_LIMIT;
        inFlight.add(offset);
        void fetchPage(offset)
          .then(() => {
            inFlight.delete(offset);
            if (!finished && !cancelled) pump();
          })
          .catch((error: unknown) => {
            inFlight.delete(offset);
            fail(error);
          });
      }
    };

    pump();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    JSON.stringify(queryKey),
    reloadKey,
    sessionEnabled,
    enabled,
    rootQuery,
    selection,
    accessToken,
    cellId,
    artifact.endpoint,
    idField,
    mobileAcquisitionId,
    locations,
  ]);

  return { ...result, refetch };
}
